import {action, computed, makeObservable, observable} from "mobx";

import {AdminApi} from "../../../api/api";
import {TemplateOutput} from "../../../api/values/database-list-output";
import {DocumentAddInput} from "../../../api/values/document-add-input";
import {DocumentBlockType, DocumentType} from "../../../api/values/document-type";
import {stringToEnum} from "../../../utils/enum-util";
import {validateLength} from "../../../utils/validation/functions/length";
import {createValidator} from "../../../utils/validation/validate";
import {ValidationResultEmpty} from "../../../utils/validation/validation-result";
import {SlugEditInputStore} from "../slug-edit-input/slug-edit-input-store";

export class DocumentAddStore {
    @observable
    public mediaId: string = "";

    @observable
    public isCreating = false;

    @observable
    public isOpen = false;

    @observable
    public title = "";
    @observable
    public blockType = DocumentBlockType.BLOCK;

    @observable
    public titleValidationResult = ValidationResultEmpty;

    @observable
    public templateId = "";

    @observable
    public templates: TemplateOutput[] = [];

    @observable
    public validationTemplateMessage: string = "テンプレート情報取得中...";

    public slugEditInputStore: SlugEditInputStore;

    private adminApi: AdminApi;

    constructor(options: {adminApi: AdminApi}, mediaId: string) {
        this.adminApi = options.adminApi;
        this.setMediaId(mediaId);
        this.slugEditInputStore = new SlugEditInputStore(
            {adminApi: options.adminApi},
            this.mediaId,
        );

        makeObservable(this);
    }

    @computed
    public get canCreate() {
        // すべて空の場合
        const isAllEmpty =
            !this.title.trim() &&
            !this.slugEditInputStore.editingSlug.trim() &&
            (this.blockType !== DocumentBlockType.FORMAT || !this.templateId.trim());

        if (isAllEmpty) {
            return false;
        }

        // タイトルバリデーションエラー
        if (this.titleValidationResult.items[0]?.errorMessage) {
            return false;
        }

        // スラッグバリデーションエラー
        if (this.slugEditInputStore.slugValidationResult.items[0]?.errorMessage) {
            return false;
        }

        // スラッグに重複があった場合
        if (this.slugEditInputStore.isSlugConflicted) {
            return false;
        }

        // blockType が FORMAT の場合は templateId 必須
        if (this.blockType === DocumentBlockType.FORMAT && !this.templateId.trim()) {
            return false;
        }

        // すべて問題なければ true
        return true;
    }

    private titleValidator = createValidator(validateLength(1, 50));

    @action.bound
    public onInteraction(nextOpenState: boolean, mediaId: string | undefined) {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this.setTitle(""); // start validation
        this.slugEditInputStore.setSlug("");
        this.slugEditInputStore.setMediaId(mediaId ?? "");
        this.isOpen = nextOpenState;
    }

    @action.bound
    public close() {
        this.isOpen = false;
    }

    @action.bound
    public async create(documentType: DocumentType, mediaId: string, onCreated: () => void) {
        await this.setIsCreating(true);
        this.setMediaId(mediaId);

        await this.adminApi.postDocument(
            {mediaId: this.mediaId},
            new DocumentAddInput({
                mediaId: this.mediaId,
                documentType: documentType,
                title: this.title,
                templateId:
                    this.blockType === DocumentBlockType.FORMAT ? this.templateId : undefined,
                slug:
                    documentType !== DocumentType.ARTICLE
                        ? this.slugEditInputStore.editingSlug
                        : "",
            }),
        );

        await this.setIsCreating(false);
        this.close();
        onCreated();
    }

    @action.bound
    public setTemplateId(value: string) {
        this.templateId = value;
    }
    @action.bound
    public setTemplates(value: TemplateOutput[]) {
        this.templates = value;
    }

    @action.bound
    private setValidationTemplateMessage(value: string) {
        this.validationTemplateMessage = value;
    }
    @action.bound
    public setBlockType(value: string) {
        const type = stringToEnum<DocumentBlockType>(DocumentBlockType, value);
        if (!type) {
            return;
        }
        this.blockType = type;
        if (this.blockType === DocumentBlockType.FORMAT) {
            this.loadTemplates();
        }
    }
    @action.bound
    public async setTitle(s: string) {
        this.title = s;
        (await this.titleValidator(s)).run((v) => (this.titleValidationResult = v));
    }

    @action.bound
    private async setIsCreating(b: boolean) {
        this.isCreating = b;
    }

    @action.bound
    public setMediaId(mediaId: string) {
        this.mediaId = mediaId;
    }
    @action.bound
    public async loadTemplates() {
        this.setValidationTemplateMessage("テンプレート情報取得中...");

        const pageSize = 1000;
        let page = 1;
        const all: TemplateOutput[] = [];
        let hasNext = true;

        try {
            while (hasNext) {
                const res = await this.adminApi.getTemplates({
                    title: "",
                    page,
                    mediaId: this.mediaId,
                    pageSize,
                });

                if (res.templates && res.templates.length > 0) {
                    all.push(...res.templates);
                }

                hasNext = res.nextPageExists;
                page += 1;
            }

            this.setTemplates(all);

            if (all.length === 0) {
                this.setTemplateId("");
                this.setValidationTemplateMessage("テンプレート情報が登録されていません。");
                return;
            }

            const exists = all.some((t) => t.id.toString() === this.templateId);
            if (!this.templateId || !exists) {
                this.setTemplateId(all[0].id.toString());
            }
        } catch (e) {
            this.setTemplates([]);
            this.setTemplateId("");
            this.setValidationTemplateMessage("テンプレート情報の取得に失敗しました");
        }
    }
}
